/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject, SCIPendingSettings} from '@ion/core/src/types/entities';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {DLSTheme} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {reportingIntervalInitialVal} from './SCISettingsUtils';

export interface SCIAdvanceTagMenuProps {
  field: GenericObject;
  classes: {
    [key: string]: string;
  };
  noUpdate: boolean;
  handleAdvanceFieldChange: Function;
  menu: string;
  value: any;
  activeTab: string;
  advanceTagOptions: Array<any>;
  initialAdvance: boolean;
  sciPendingSettingData: SCIPendingSettings;
  handleTagDetectionMode: Function;
}

const textFieldStyleOverrides = (theme: DLSTheme) => ({
  floatingLabel : {
    color : theme.dls.colors.grey3
  },
  root : {
    '&&' : {
      width     : '66%',
      margin    : '10px 0 10px 130px',
      '&:hover' : {
        backgroundColor : 'transparent'
      }
    }
  },
  underline : {
    backgroundColor : 'transparent'
  },
  inputRoot : {
    '& input' : {
    },
    '&:hover' : {
      backgroundColor : 'transparent'
    }
  }
});

const dropDownStyle = {
  width : '56%'
};

// defines the names of Tag Detection fields
export enum tagDetectionFields {
  zMicroRxPeriod = 'zMicroRxPeriod',
  zMicroRxTime = 'zMicroRxTime'
};
/**
 * View or Edit Advance Sub Menu Tag related information of an SCI device
 */
export const SCIAdvanceTagMenu = ({
  field,
  classes,
  noUpdate,
  menu,
  value,
  activeTab,
  handleAdvanceFieldChange,
  advanceTagOptions,
  initialAdvance,
  sciPendingSettingData,
  handleTagDetectionMode
}: SCIAdvanceTagMenuProps) => {
  const isTagDetectionMode: boolean = field.label === translate('admin-devices:view.sciSettings.tagDetectionMode');
  const dropDownValue = initialAdvance ? sciPendingSettingData[menu] : value;
  const isTagEditable = useCallback((key: string, val: number): boolean => {
    if (key === tagDetectionFields.zMicroRxPeriod) {
      return val === 0 || val === 1;
    } else if (key === tagDetectionFields.zMicroRxTime) {
      return val === 0;
    }
    return false;
  }, []);
  if (isTagDetectionMode) {
    return (
      <FormItem fullWidth>
        <div className={classes.actionsTile}>
          <BodyTypography className={classes.overridesReportTitle}>
            {field.label}
          </BodyTypography>
          <div style={dropDownStyle}>
            <Dropdown displaySelectedLabel
              options={advanceTagOptions}
              value={reportingIntervalInitialVal(menu, dropDownValue)}
              disabled={noUpdate}
              onChange={handleTagDetectionMode}/>
          </div>
        </div>
      </FormItem>
    );
  }
  return (
    <FormItem fullWidth>
      <div className={classes.actionsTile}>
        <BodyTypography className={classes.overridesReportTitle}>
          {field.label}
        </BodyTypography>
        <FormTextField name={field.label}
          disabled={isTagEditable(menu, value) || noUpdate}
          value={value}
          variant='standard'
          type='number'
          styleOverrides={textFieldStyleOverrides}
          onChange={function(e) {
            handleAdvanceFieldChange(e, field, activeTab);
          }}/>
      </div>
    </FormItem>
  );
};

export default SCIAdvanceTagMenu;
